// JournalArticleForm migration: 1.2 Article — default empty data uses article/journal/issue shape
import cloneDeep from 'lodash/cloneDeep';
import { ContentRegistrationType } from '@/common/types';
import merge from 'lodash/merge';
import { migrateFormDataLanguageCodes } from '@content-registration/utils/crossrefLanguageCodes';

// Predefined defaults for books
const defaultBookFormData = {
  bookMetadata: {
    contributors: [{}],
    abstracts: [{}],
    publicationDates: {},
  },
};

// Predefined defaults for reports
const defaultReportFormData = {
  reportMetadata: {
    contributors: [{}],
    abstracts: [{}],
    publicationDates: {},
  },
};

// Predefined defaults for dissertations
const defaultDissertationFormData = {
  dissertationMetadata: {
    contributors: [{}],
    approvingInstitution: {},
  },
};

const defaultGrantFormData = {
  grantMetadata: {
    award_amount: {},
    funding: {},
    'project-title': [{}],
    description: [],
    investigators: [],
  },
};

const defaultJournalArticleJournalMetadata = {
  titles: {},
  issn: {},
};

const defaultJournalArticleFormData = {
  article: {
    contributors: [{}],
    // JournalArticleForm migration: 1.2 Abstract
    abstract: [{}],
    // JournalArticleForm migration: 1.2 Publication Dates
    publicationDates: {},
    // JournalArticleForm migration: 1.2 Funding
    funding: [],
    // JournalArticleForm migration: 1.2 Relationships
    relationships: [],
  },
  // JournalArticleForm migration: 1.3 Journal step
  journal: defaultJournalArticleJournalMetadata,
};

/**
 * Applies predefined defaults to form data based on the form type.
 * Initial data is merged on top of the type-specific default shape, then
 * language codes are migrated to Crossref-compatible values.
 *
 * @param formType - The type of content registration form (e.g., grant, journal article).
 * @param initialData - Initial form data to use as a baseline.
 * @returns The form data with defaults applied based on form type.
 */
const withMigratedLanguageCodes = <T>(data: T): T =>
  migrateFormDataLanguageCodes(cloneDeep(data));

export const getFormDataWithDefaults = (
  formType: ContentRegistrationType,
  initialData: any
) => {
  if (formType === ContentRegistrationType.JournalArticle) {
    return withMigratedLanguageCodes(
      merge({}, defaultJournalArticleFormData, initialData)
    );
  } else if (formType === ContentRegistrationType.Book) {
    return withMigratedLanguageCodes(
      merge({}, defaultBookFormData, initialData)
    );
  } else if (formType === ContentRegistrationType.Dissertation) {
    return withMigratedLanguageCodes(
      merge({}, defaultDissertationFormData, initialData)
    );
  } else if (formType === ContentRegistrationType.Grant) {
    return withMigratedLanguageCodes(
      merge({}, defaultGrantFormData, initialData)
    );
  } else if (formType === ContentRegistrationType.Report) {
    return withMigratedLanguageCodes(
      merge({}, defaultReportFormData, initialData)
    );
  } else {
    return withMigratedLanguageCodes(initialData || {});
  }
};
